import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CATEGORY_GRADIENTS,
  CATEGORY_BADGES,
  categoryToId,
  isoLeadAuditorCourses,
} from "./isoLeadAuditorData";
import { ICON_MAP, ArrowRightIcon, BookOpenIcon } from "./isoIcons";
import CertificationLogos from "../../components/CertificationLogos";
import { courseLogoKey } from "./courseLogos";

/* ============================================================================
   The course card grid, grouped by category.

   Lifted out of IsoLeadAuditorTraining on 2026-09-14 so the per-service pages
   and the full listing render from ONE copy of this markup. Pass `categories`
   to control what is shown: the full listing passes every category, a service
   page passes just its own — the page decides, this component does not.

   The cards follow the Services page's standards cards: the real certification
   mark on top where the project holds one, then the code and the programme
   name. 13 of the 103 codes have a mark; the rest keep their icon in the brand
   chip rather than being given a look-alike badge.
   ========================================================================= */
/* headingStyle "centered" (the Services dropdown pages): a centred section
   heading in the site's own style — brand-purple title, orange rule, and the
   programme count as a quiet line beneath. The default keeps the compact
   left-aligned label used on the all-courses page. */
const IsoCourseGrid = ({ categories, headingStyle = "inline" }) => (
  <section className="py-14 px-4 sm:px-6 lg:px-8 bg-gray-50">
    <div className="max-w-7xl mx-auto">
      {categories.map((category, categoryIndex) => {
        const courses = isoLeadAuditorCourses.filter(
          (course) => course.category === category
        );
        if (!courses.length) return null;

        return (
          <motion.div
            key={category}
            id={categoryToId(category)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.05 * categoryIndex }}
            className="mb-12 last:mb-0 scroll-mt-32"
          >
            {headingStyle === "centered" ? (
              <div className="mb-10 text-center lg:mb-12">
                <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[var(--brand-primary,#682e92)]">
                  {category}
                </h2>
                <div className="mx-auto mt-3 h-1 w-24 rounded-full bg-[#F86624]" aria-hidden="true" />
                <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#f5eef9] px-4 py-1.5 text-sm font-semibold text-[var(--brand-primary,#682e92)]">
                  {courses.length} Training Programs
                </p>
              </div>
            ) : (
            <>
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <div
                className={`w-1.5 h-8 rounded-full bg-gradient-to-b ${CATEGORY_GRADIENTS[category]}`}
                aria-hidden="true"
              />
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                {category}
              </h2>
              <span
                className={`text-xs font-medium px-2.5 py-1 rounded-full border ${CATEGORY_BADGES[category]}`}
              >
                {courses.length} Programs
              </span>
            </div>
            <div className="w-16 h-1 bg-[#F86624] rounded-full -mt-2 mb-6" aria-hidden="true" />
            </>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {courses.map((course, courseIndex) => {
                const Icon = ICON_MAP[course.icon] || BookOpenIcon;
                const logoKey = courseLogoKey(course.iso);
                const card = (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.03 * courseIndex }}
                    className={`brand-card h-full p-6 ${
                      course.link ? "cursor-pointer" : ""
                    }`}
                  >
                    {/* the standard's own mark where one exists, otherwise the
                        course icon in the brand chip — never a stand-in mark */}
                    <CertificationLogos
                      logoKeys={logoKey ? [logoKey] : []}
                      label={`${course.iso} certification mark`}
                      className="flex h-[4.5rem] items-center"
                      size="h-[4.5rem]"
                      fallback={
                        <span className="flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-[var(--brand-primary)] text-white shadow-md">
                          <Icon size={32} aria-hidden="true" />
                        </span>
                      }
                    />

                    <h3 className="mt-4 text-base lg:text-lg font-bold leading-snug text-gray-900">
                      {course.iso}
                    </h3>
                    <p className="mt-2 text-sm md:text-base leading-relaxed text-gray-600">
                      {course.title}
                    </p>

                    {course.link && (
                      <span className="mt-auto flex items-center gap-2 pt-5 text-sm font-semibold text-[var(--brand-primary)]">
                        View programme
                        <ArrowRightIcon size={14} aria-hidden="true" />
                      </span>
                    )}
                  </motion.div>
                );

                return course.link ? (
                  <Link
                    key={course.iso}
                    to={course.link}
                    className="block h-full"
                    aria-label={`${course.iso} — ${course.title}`}
                  >
                    {card}
                  </Link>
                ) : (
                  <div key={course.iso} className="h-full">
                    {card}
                  </div>
                );
              })}
            </div>
          </motion.div>
        );
      })}
    </div>
  </section>
);

export default IsoCourseGrid;
